// #95 (S1 of the 22-language plan): the one registry of the UI languages.
// en and ro are bundled with the app (i18n/index.ts); every other language
// registers its static translation as a lazy chunk in `lazyLocales`, loaded
// by the language coordinator (utils/helpers `setLanguage`) on selection.
// #96 (S2): the 20 further languages, one code-split chunk each
// (`locale-<code>`; checked over build/ by `npm run check:locale-chunks`).

export interface SupportedLanguage {
  code: string;
  // shown in the switchers: the upper-case code (no native names)
  name: string;
}

export type Translation = Record<string, unknown>;

export const DEFAULT_LANGUAGE = 'en';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: 'en', name: 'EN' },
  { code: 'ro', name: 'RO' },
  { code: 'bg', name: 'BG' },
  { code: 'cs', name: 'CS' },
  { code: 'da', name: 'DA' },
  { code: 'de', name: 'DE' },
  { code: 'el', name: 'EL' },
  { code: 'es', name: 'ES' },
  { code: 'et', name: 'ET' },
  { code: 'fi', name: 'FI' },
  { code: 'fr', name: 'FR' },
  { code: 'hr', name: 'HR' },
  { code: 'hu', name: 'HU' },
  { code: 'it', name: 'IT' },
  { code: 'lt', name: 'LT' },
  { code: 'lv', name: 'LV' },
  { code: 'nl', name: 'NL' },
  { code: 'pl', name: 'PL' },
  { code: 'pt', name: 'PT' },
  { code: 'sk', name: 'SK' },
  { code: 'sl', name: 'SL' },
  { code: 'sv', name: 'SV' },
];

// code -> dynamic import of its static translation (default export); en and
// ro are bundled and never listed here
export const lazyLocales: Record<
  string,
  () => Promise<{ default: Translation }>
> = {
  bg: () =>
    import(
      /* webpackChunkName: "locale-bg" */ '../locales/bg/translation.json'
    ),
  cs: () =>
    import(
      /* webpackChunkName: "locale-cs" */ '../locales/cs/translation.json'
    ),
  da: () =>
    import(
      /* webpackChunkName: "locale-da" */ '../locales/da/translation.json'
    ),
  de: () =>
    import(
      /* webpackChunkName: "locale-de" */ '../locales/de/translation.json'
    ),
  el: () =>
    import(
      /* webpackChunkName: "locale-el" */ '../locales/el/translation.json'
    ),
  es: () =>
    import(
      /* webpackChunkName: "locale-es" */ '../locales/es/translation.json'
    ),
  et: () =>
    import(
      /* webpackChunkName: "locale-et" */ '../locales/et/translation.json'
    ),
  fi: () =>
    import(
      /* webpackChunkName: "locale-fi" */ '../locales/fi/translation.json'
    ),
  fr: () =>
    import(
      /* webpackChunkName: "locale-fr" */ '../locales/fr/translation.json'
    ),
  hr: () =>
    import(
      /* webpackChunkName: "locale-hr" */ '../locales/hr/translation.json'
    ),
  hu: () =>
    import(
      /* webpackChunkName: "locale-hu" */ '../locales/hu/translation.json'
    ),
  it: () =>
    import(
      /* webpackChunkName: "locale-it" */ '../locales/it/translation.json'
    ),
  lt: () =>
    import(
      /* webpackChunkName: "locale-lt" */ '../locales/lt/translation.json'
    ),
  lv: () =>
    import(
      /* webpackChunkName: "locale-lv" */ '../locales/lv/translation.json'
    ),
  nl: () =>
    import(
      /* webpackChunkName: "locale-nl" */ '../locales/nl/translation.json'
    ),
  pl: () =>
    import(
      /* webpackChunkName: "locale-pl" */ '../locales/pl/translation.json'
    ),
  pt: () =>
    import(
      /* webpackChunkName: "locale-pt" */ '../locales/pt/translation.json'
    ),
  sk: () =>
    import(
      /* webpackChunkName: "locale-sk" */ '../locales/sk/translation.json'
    ),
  sl: () =>
    import(
      /* webpackChunkName: "locale-sl" */ '../locales/sl/translation.json'
    ),
  sv: () =>
    import(
      /* webpackChunkName: "locale-sv" */ '../locales/sv/translation.json'
    ),
};

// `ro-RO`, `RO`, ` ro ` -> `ro`; anything not registered -> null
export const normaliseLanguage = (input: unknown): string | null => {
  if (typeof input !== 'string') return null;
  const base = input.trim().toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.some(l => l.code === base) ? base : null;
};

// The language a visit starts in, decided once per entry:
// Both embedded and full-screen QR flows use the normalised URL
// `default-language`, then the first supported browser language, then English.
// A language cached by an older build in the browser storage never decides.
export const resolveEntryLanguage = (
  search: string,
  browserLanguages: readonly string[]
): string =>
  normaliseLanguage(new URLSearchParams(search).get('default-language')) ??
  browserLanguages.map(normaliseLanguage).find(Boolean) ??
  DEFAULT_LANGUAGE;
